// Custom React Flow node for deployable resources (EC2, RDS, S3, Cloud SQL,
// Azure SQL, etc.). Compact card with a provider tier color stripe and a
// monthly cost chip when available.
//
// Phase 4+5 additions: context menu trigger + peer connect candidate state
// + dim/highlight visuals when a drag/placement/peer-connect mode is active.

import { Handle, Position } from "@xyflow/react";
import { Cpu, Database, Globe, Lock, Network } from "lucide-react";

const TIER_COLORS = {
  frontend: "#5B5BD6",
  compute: "#0EA5E9",
  data: "#0D9488",
  networking: "#F59E0B",
  security: "#EF4444",
};

function TierIcon({ tier, color }) {
  const Icon =
    tier === "data"
      ? Database
      : tier === "networking"
      ? Network
      : tier === "security"
      ? Lock
      : tier === "frontend"
      ? Globe
      : Cpu;
  return <Icon size={14} strokeWidth={2.25} color={color} />;
}

export default function ResourceNode({ id, data }) {
  const tier = (data.config?.tier || "compute").toLowerCase();
  const color = TIER_COLORS[tier] || "#0EA5E9";
  const cost = data.config?.estMonthlyCost;

  let borderColor = "var(--color-border-primary)";
  let outline = "none";
  if (data.invalid) {
    borderColor = "var(--color-danger, #ef4444)";
    outline = "2px solid color-mix(in oklab, var(--color-danger, #ef4444) 85%, transparent)";
  } else if (data.peerSource) borderColor = "var(--color-accent)";
  else if (data.peerTargetCandidate) borderColor = "color-mix(in oklab, var(--color-accent) 60%, transparent)";

  return (
    <div
      data-testid={`arch-node-resource-${data.type}`}
      data-arch-node-id={id}
      className="motion-micro"
      style={{
        width: 220,
        minHeight: 76,
        background: "var(--color-background-secondary)",
        borderTopWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderLeftWidth: 3,
        borderStyle: "solid",
        borderTopColor: borderColor,
        borderRightColor: borderColor,
        borderBottomColor: borderColor,
        borderLeftColor: color,
        borderRadius: 10,
        outline,
        outlineOffset: 2,
        opacity: data.dimmed ? 0.35 : 1,
        boxShadow: data.selected
          ? `0 0 0 2px color-mix(in oklab, var(--color-accent) 55%, transparent), 0 12px 24px -16px rgba(0,0,0,0.55)`
          : "0 4px 12px -10px rgba(0,0,0,0.5)",
        padding: "10px 12px",
        cursor: data.peerTargetCandidate ? "copy" : "pointer",
      }}
      onContextMenu={(e) => {
        if (data.onContextMenu) {
          e.preventDefault();
          e.stopPropagation();
          data.onContextMenu(id, e.clientX, e.clientY);
        }
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div className="flex items-center gap-2 mb-1.5">
        <TierIcon tier={tier} color={color} />
        <span
          className="font-mono"
          style={{
            fontSize: 9,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color,
            fontWeight: 600,
          }}
        >
          {tier}
        </span>
      </div>
      <div
        className="truncate"
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--color-text-primary)",
          letterSpacing: "-0.005em",
        }}
        title={data.name}
      >
        {data.name}
      </div>
      {typeof cost === "number" && cost > 0 && (
        <div
          className="font-mono mt-1"
          style={{ fontSize: 10, color: "var(--color-text-tertiary)" }}
        >
          ${cost}/mo
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}
